// Typed errors for the three API responses the whole app handles the same way
// no matter which endpoint produced them: a permission denial (HTTP 403), an
// expired/invalid session (HTTP 401), and a disabled store (HTTP 402). The
// fetch wrapper throws these from a single place (see fetch-wrapper.ts), the
// QueryClient's global cache handler turns them into a single toast or a
// route-level takeover (see client-config.ts + alert-context.tsx), and
// addErrorAlert drops the per-hook duplicate when the caller hands it the error
// as `cause`, so they don't double-toast or get logged to Datadog as errors.
//
// Adding a fourth means: a message constant, an Error subclass, and one clause
// in isExpectedApiError. Nothing keys on the message text.

/**
 * Shown when the API rejects an action the signed-in user isn't allowed to do (403).
 *
 * Names a remedy rather than stopping at the refusal. This toast is usually the
 * only thing the user sees, and the surfaces that know *which* permission is
 * missing say so in place (see data/permissions.ts) — so the job here is to
 * cover everything else with a next step instead of a dead end.
 */
export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to do that. Ask an owner of this store to update your access.";

/** Shown when the session is gone and the user needs to sign in again (401). */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/** Shown when the store's account is not in good standing (402). */
export const STORE_DISABLED_MESSAGE =
  'This store is on hold. Update your billing details to re-enable it.';

export class PermissionError extends Error {
  constructor(message: string = PERMISSION_DENIED_MESSAGE) {
    super(message);
    this.name = 'PermissionError';
  }
}

export class SessionExpiredError extends Error {
  constructor(message: string = SESSION_EXPIRED_MESSAGE) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

export class StoreDisabledError extends Error {
  constructor(message: string = STORE_DISABLED_MESSAGE) {
    super(message);
    this.name = 'StoreDisabledError';
  }
}

/** True for the two errors the global cache handler presents on its own. */
export function isApiAuthError(error: unknown): error is PermissionError | SessionExpiredError {
  return error instanceof PermissionError || error instanceof SessionExpiredError;
}

/**
 * True for every API error the app presents on its own and should never retry:
 * the two auth errors plus store-disabled. Retrying any of them just replays
 * the same rejection.
 */
export function isExpectedApiError(error: unknown): boolean {
  return isApiAuthError(error) || error instanceof StoreDisabledError;
}

/**
 * Logs a breadcrumb for a failure, unless it's one of the expected API errors.
 *
 * datadogLogs is initialised with `forwardConsoleLogs: ['log', 'warn', 'error']`,
 * so a raw console.error becomes a status:error log whatever threw — a second
 * path into Datadog alongside addErrorAlert's own reporting. Every catch that
 * logs a breadcrumb has to keep expected account states out of it, so the rule
 * lives here once instead of at each call site.
 */
export function logUnexpectedApiError(label: string, error: unknown): void {
  if (isExpectedApiError(error)) {
    return;
  }

  console.error(label, error);
}

// Bridge between the QueryClient cache handlers (created at module load, outside
// React) and the alert context (a React provider). AlertContextProvider registers
// its toast callback on mount; the cache handlers call reportGlobalApiError.
type GlobalApiErrorHandler = (error: PermissionError | SessionExpiredError) => void;

let globalApiErrorHandler: GlobalApiErrorHandler | null = null;

export function registerGlobalApiErrorHandler(handler: GlobalApiErrorHandler | null): void {
  globalApiErrorHandler = handler;
}

// A second, separate bridge. The auth bridge's only consumer maps it to a
// warning toast; store-disabled drives a route-level takeover instead, and
// routing it through the auth bridge would produce a toast AND a takeover.
type StoreDisabledHandler = (error: StoreDisabledError) => void;

let storeDisabledHandler: StoreDisabledHandler | null = null;

export function registerStoreDisabledHandler(handler: StoreDisabledHandler | null): void {
  storeDisabledHandler = handler;
}

/**
 * Cache-level onError entry point: routes a disabled store to the takeover and
 * auth/permission errors to their toast. Everything else is left to the hook
 * that made the call.
 */
export function reportGlobalApiError(error: unknown): void {
  if (error instanceof StoreDisabledError) {
    storeDisabledHandler?.(error);

    return;
  }

  if (isApiAuthError(error)) {
    globalApiErrorHandler?.(error);
  }
}
